/**
 * Client-error reporting — the browser's half of it.
 *
 * ---------------------------------------------------------------------------
 * ADR 0011 and why this payload looks like this
 * ---------------------------------------------------------------------------
 *
 * ADR 0011 §"Strengthened in Task 1 review" is unambiguous about two things:
 * the guarantee is an ALLOWLIST at the logging boundary, and an error's
 * `message`/`stack` are never part of it — `sanitizeError()` reduces an error to
 * `{ errorType, errorCode?, statusCode?, messageWithheld: true }` precisely
 * because messages "routinely embed connection strings, SQL fragments and
 * response bodies". In this product they can also quote a client's name or a
 * worker's coordinates, because the message is whatever the throwing code was
 * holding at the time.
 *
 * The obvious way to ship browser errors — widen the payload with the message
 * and stack, and add a redactor to the bundle — was rejected. A browser-side
 * redactor is a DENYLIST over free text (ADR 0011 exists to argue against
 * exactly that), it is unenforceable because the server would have to trust the
 * client that ran it, and it ships to the attacker along with everything else.
 * ADR 0011's own words on the alternative: stack traces "belong in an
 * error-tracking destination with its own scrubbing, not in general logs".
 *
 * So this is the opaque form. What crosses the wire is IDENTIFIERS AND CODE
 * LOCATIONS, never the message:
 *
 *   id          a random correlation id, generated in the tab. The report is
 *               POSTed with this as its `x-request-id`, so it becomes the API's
 *               own request id and lands on every server log line for the
 *               report. The user is shown the same string. That is the whole
 *               join: "reference 0f3c…" → one Cloud Logging entry.
 *   source      render | window | promise — which failure path.
 *   errorType   the constructor name ('TypeError', 'ApiError'). An identifier.
 *   errorCode   an application code where the error carried one.
 *   statusCode  the HTTP status, for a failure that came from an API call.
 *   requestId   the `x-request-id` of the API call that failed, when the error
 *               was one. This is the second join, and the valuable one: the
 *               server already logged that request in full (sanitized), so the
 *               DETAIL is reconstructed server-side from data the server itself
 *               produced — never from anything the browser sends.
 *   route       the client route, with ids masked. Location, not content.
 *   release     the hashed bundle filename, so frames mean something.
 *   frames      file/line/column only, parsed out of the stack and nothing else.
 *               Code, not data — and the message line of the stack is never
 *               parsed at all (see errorReporting.ts).
 *
 * The message stays in the tab, shown to the person in front of it. It is their
 * own screen and their own data; it just never becomes a log line.
 */
import { post } from './transport.js';
/**
 * POST one report to the API.
 *
 * The report's own id is sent as `x-request-id` so the value the user is shown
 * and the value in Cloud Logging are the same value. Unauthenticated is fine
 * and deliberate: a crash on the sign-in screen is exactly the kind nobody can
 * otherwise debug. The API attributes org/user from the session when there is
 * one and never from this body.
 */
export function report(body) {
    return post('/api/client-errors', body, {
        headers: { 'x-request-id': body.id },
    });
}
//# sourceMappingURL=clientErrors.js.map